/**
 * Aceleres: a lista de mensagens fixadas de uma conversa.
 *
 * A conversa guarda a lista em `additional_attributes.pinned_message_ids` e um
 * instantâneo de cada uma em `pinned_messages` — o banner do topo precisa saber
 * o TIPO do conteúdo (texto, imagem, vídeo, GIF) mesmo quando a mensagem ainda
 * não foi carregada na tela.
 *
 * O formato antigo era um id solto (`pinned_message_id`). Conversas fixadas
 * antes desta mudança continuam com ele e nunca serão reescritas até alguém
 * fixar ou desafixar algo, então toda leitura aceita as duas formas. Este
 * módulo é o único lugar que sabe disso: o banner, o alfinete no balão, o menu
 * de contexto, a galeria e o visualizador de PDF perguntam aqui, e por isso
 * não podem discordar entre si.
 *
 * Cada fixada tem um prazo (`pinned_message_untils`), escolhido no modal na
 * hora de fixar. A leitura aqui já descarta o que venceu, então a fixada some
 * na hora certa mesmo antes de o cron do servidor
 * (Aceleres::ExpirePinnedMessagesJob) reescrever a conversa. Por isso as
 * funções aceitam um `now`: quem precisa reavaliar com o relógio andando passa
 * um valor que muda (ver o banner).
 */

// Teto de fixadas por conversa. O mesmo número está no controller
// (MAX_PINNED_MESSAGES em message_actions_controller.rb).
export const MAX_PINNED_MESSAGES = 4;

// Períodos de fixação oferecidos pelo modal, na ordem em que ele os desenha.
// As chaves são as mesmas do backend (DURATIONS em pinned_messages.rb) — é
// isso que o `duration` do POST /pin carrega.
export const PIN_DURATIONS = [
  { value: '24h', labelKey: 'CONVERSATION.PIN_DURATION.OPTIONS.24H' },
  { value: '7d', labelKey: 'CONVERSATION.PIN_DURATION.OPTIONS.7D' },
  { value: '30d', labelKey: 'CONVERSATION.PIN_DURATION.OPTIONS.30D' },
];

export const DEFAULT_PIN_DURATION = '24h';

const attrsOf = conversation => conversation?.additional_attributes || {};

// Quando cada fixada expira, na chave string do id — o backend grava em
// `pinned_message_untils` a data escolhida no modal.
const untilsOf = conversation =>
  attrsOf(conversation).pinned_message_untils || {};

/**
 * A fixada venceu? Um prazo ausente nunca vence: é o que as conversas fixadas
 * antes do modal existir têm, e elas continuam fixadas até alguém desafixar.
 */
const isExpired = (untils, id, now) => {
  const until = untils[String(id)];
  return !!until && new Date(until).getTime() <= now;
};

/**
 * Ids fixados, na ordem em que foram fixados (o mais antigo primeiro).
 */
export const pinnedMessageIds = (conversation, now = Date.now()) => {
  const attrs = attrsOf(conversation);
  const untils = untilsOf(conversation);
  const list = Array.isArray(attrs.pinned_message_ids)
    ? attrs.pinned_message_ids
    : [attrs.pinned_message_id];

  return list
    .filter(id => id !== null && id !== undefined && id !== '')
    .map(Number)
    .filter(id => !Number.isNaN(id))
    .filter(id => !isExpired(untils, id, now))
    .slice(0, MAX_PINNED_MESSAGES);
};

/**
 * Instantâneos guardados na conversa, um por id fixado e na mesma ordem.
 * Quando só existe o formato antigo, monta um instantâneo do que havia.
 */
export const pinnedMessageSnapshots = (conversation, now = Date.now()) => {
  const attrs = attrsOf(conversation);
  const ids = pinnedMessageIds(conversation, now);
  const stored = Array.isArray(attrs.pinned_messages)
    ? attrs.pinned_messages
    : [attrs.pinned_message].filter(Boolean);

  const byId = new Map(
    stored.filter(item => item?.id != null).map(item => [Number(item.id), item])
  );

  const untils = untilsOf(conversation);

  return ids.map(id => ({
    ...(byId.get(id) || {}),
    id,
    pinned_until: untils[String(id)] || null,
  }));
};

export const isMessagePinned = (conversation, messageId) => {
  if (messageId === null || messageId === undefined) return false;
  return pinnedMessageIds(conversation).includes(Number(messageId));
};

export const pinnedMessageCount = conversation =>
  pinnedMessageIds(conversation).length;

export const isPinLimitReached = conversation =>
  pinnedMessageCount(conversation) >= MAX_PINNED_MESSAGES;
